import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type EditionName = 'community' | 'hosted' | 'commercial-self-hosted'

export interface PlatformBranding {
  name: string
  logo_url: string | null
  banner_url: string | null
  tagline: string | null
  /**
   * Whether Public Facing Lobby mode is currently armed. When true, the
   * router pushes any non-bypass route through `/lobby` first. Reflects the
   * platform's authoritative state — never trust a client-side flag for
   * gating; the server-side plugin remains the boundary.
   */
  lobby_enabled: boolean
  /** Optional banner image shown on the lobby page, set under Branding. */
  lobby_image_url: string | null
  /** Edition surface for the universal footer pill. License details (id,
   *  contact, expiry) stay behind the admin license endpoint. */
  edition: { name: EditionName; licensed_org: string | null }
}

const DEFAULT: PlatformBranding = {
  name: 'Workshop Platform',
  logo_url: null,
  banner_url: null,
  tagline: null,
  lobby_enabled: false,
  lobby_image_url: null,
  edition: { name: 'community', licensed_org: null },
}

const BrandingContext = createContext<PlatformBranding>(DEFAULT)

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<PlatformBranding>(DEFAULT)
  useEffect(() => {
    let cancelled = false
    fetch('/api/platform', { credentials: 'include' })
      .then(async (res) => (res.ok ? ((await res.json()) as PlatformBranding) : DEFAULT))
      .then((data) => {
        if (!cancelled) setBranding(data ?? DEFAULT)
      })
      .catch(() => {
        if (!cancelled) setBranding(DEFAULT)
      })
    return () => {
      cancelled = true
    }
  }, [])
  useEffect(() => {
    if (branding.name) document.title = branding.name
  }, [branding.name])
  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>
}

export function useBranding(): PlatformBranding {
  return useContext(BrandingContext)
}

interface BrandMarkProps {
  size?: 'sm' | 'md' | 'lg'
  /**
   * Whether to render the platform name next to the mark.
   * Default: `false` when a logo image is present (since branded assets typically
   * have the wordmark baked in), `true` when falling back to the generic mark.
   */
  showName?: boolean
  className?: string
}

export function BrandMark({ size = 'md', showName, className = '' }: BrandMarkProps) {
  const { name, logo_url, banner_url } = useBranding()

  // For the large slot, prefer the horizontal banner if provided.
  const imgSrc = size === 'lg' ? (banner_url ?? logo_url) : logo_url
  const hasImage = imgSrc !== null
  const renderName = showName ?? !hasImage

  const heightClass = size === 'lg' ? 'h-12' : size === 'sm' ? 'h-6' : 'h-8'
  const nameClass =
    size === 'lg'
      ? 'text-2xl font-semibold tracking-tight'
      : size === 'sm'
        ? 'text-sm font-medium'
        : 'text-base font-semibold'

  return (
    <span className={`inline-flex items-center gap-3 ${className}`}>
      {hasImage ? (
        <img src={imgSrc} alt={name} className={`${heightClass} w-auto rounded-sm`} />
      ) : (
        <DefaultMark size={size} />
      )}
      {renderName && <span className={`${nameClass} text-slate-900 dark:text-slate-50`}>{name}</span>}
    </span>
  )
}

function DefaultMark({ size }: { size: 'sm' | 'md' | 'lg' }) {
  const dim = size === 'lg' ? 48 : size === 'sm' ? 24 : 32
  return (
    <span
      aria-hidden
      className="inline-grid place-items-center rounded-md bg-emerald-100 text-emerald-700 ring-1 ring-emerald-500 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/40"
      style={{ width: dim, height: dim }}
    >
      <svg viewBox="0 0 24 24" width={dim * 0.65} height={dim * 0.65} fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 7l8-4 8 4-8 4-8-4z" />
        <path d="M4 12l8 4 8-4" />
        <path d="M4 17l8 4 8-4" />
      </svg>
    </span>
  )
}
